import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ListChecks, ShieldCheck, BarChart3, Wallet, Zap, Copy, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { copyToClipboard } from "@/lib/clipboard";
import OnboardingTour from "@/components/dashboard/OnboardingTour";
import VerifyEmailBanner from "@/components/dashboard/VerifyEmailBanner";

export default function PublisherOverview() {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [testIngestBusy, setTestIngestBusy] = useState(false);
  const [testIngestResult, setTestIngestResult] = useState(null);

  const refresh = async () => {
    try {
      const { data } = await api.get("/publisher/overview");
      setOverview(data);
    } catch (e) {
      toast.error("Failed to load publisher overview");
    }
  };
  useEffect(() => { refresh(); }, []);

  const runTestIngest = async () => {
    setTestIngestBusy(true);
    try {
      const { data } = await api.post("/publisher/test-ingest");
      setTestIngestResult(data);
      toast.success("Test lead created — check your LIVE feed!");
      setTimeout(() => refresh(), 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test ingest failed");
    } finally {
      setTestIngestBusy(false);
    }
  };

  if (!overview) return <div className="min-h-[40vh] grid place-items-center text-sm text-slate-500"><Loader2 className="size-4 animate-spin" /></div>;

  const k = overview.kpis;
  const ingestUrl = `${BACKEND_URL}/api/v1/leads/ingest`;

  return (
    <>
      <OnboardingTour role="publisher" />
      <VerifyEmailBanner />

      <div className="space-y-8" data-testid="publisher-overview-page">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">Welcome back, {user?.name?.split(" ")[0]}</h1>
          <p className="mt-1 text-sm text-slate-600">Push leads → we route them → you earn per accepted lead.</p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi Icon={ListChecks} label="Leads sent" value={k.leads_total} />
          <Kpi Icon={ShieldCheck} label="Accept rate" value={`${k.accept_rate_pct.toFixed(1)}%`} tint="emerald" />
          <Kpi Icon={BarChart3} label="Earnings (gross)" value={`£${k.earnings_total_gbp.toLocaleString()}`} />
          <Kpi Icon={Wallet} label="Available to withdraw" value={`£${k.available_balance_gbp.toLocaleString()}`} tint="brand" />
        </div>

        {/* Quickstart strip — short version; full curl lives on /publisher/api-keys */}
        <div className="rounded-xl border border-slate-200 bg-white shadow-soft p-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Push a test lead</h2>
              <p className="text-sm text-slate-600 mt-1">Sends a synthetic lead through the full routing pipeline — no wallet movement.</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => copyToClipboard(ingestUrl).then(() => toast.success("Endpoint copied"))} data-testid="pub-copy-endpoint">
                <Copy className="size-3.5" /> Copy ingest URL
              </Button>
              <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={runTestIngest} disabled={testIngestBusy} data-testid="pub-test-ingest">
                {testIngestBusy ? <><Loader2 className="size-3.5 animate-spin" /> Sending…</> : <><Zap className="size-3.5" /> Send test lead</>}
              </Button>
              <Link to="/publisher/api-keys" className="text-[12.5px] text-[#0055FF] font-medium hover:underline inline-flex items-center gap-1">
                Full Quickstart <ArrowRight className="size-3" />
              </Link>
            </div>
          </div>
          {testIngestResult && (
            <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 text-[12.5px] text-emerald-900" data-testid="pub-test-ingest-result">
              <span className="font-semibold">Test lead created ✓</span> · ID <code className="font-mono">{testIngestResult.lead_id?.slice(0, 8)}</code> · Sector {testIngestResult.sector} · Buyer would pay £{testIngestResult.price_gbp} · You'd earn £{testIngestResult.publisher_earning_gbp} ({testIngestResult.rev_share_pct}% share).
            </div>
          )}
        </div>

        {/* Limits + Recent leads */}
        <div className="grid lg:grid-cols-2 gap-5">
          <div className="rounded-xl border border-slate-200 bg-white shadow-soft p-6" data-testid="pub-limits-panel">
            <h2 className="text-lg font-semibold text-slate-900">Your account limits</h2>
            <p className="text-[12.5px] text-slate-500 mt-1">Set by Connexis admin. Contact your account manager to adjust.</p>
            <div className="mt-4 space-y-2 text-sm">
              <Row k="Revenue share" v={`${overview.limits?.revenue_share_pct ?? 90}%`} />
              <Row k="Daily lead cap" v={overview.limits?.daily_lead_cap ? `${overview.limits.daily_lead_cap.toLocaleString()} / day` : "Unlimited"} />
              <Row k="30-day lead cap" v={overview.limits?.monthly_lead_cap ? `${overview.limits.monthly_lead_cap.toLocaleString()} / 30d` : "Unlimited"} />
              <Row k="Status" v={overview.limits?.active === false ? "Paused" : "Active"} />
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white shadow-soft p-6">
            <h2 className="text-lg font-semibold text-slate-900">Recent leads</h2>
            <div className="mt-4 space-y-2 text-sm max-h-80 overflow-y-auto">
              {(overview.recent_leads || []).length === 0 ? (
                <p className="text-[12.5px] text-slate-400 italic">No leads yet — push your first via the API.</p>
              ) : overview.recent_leads.map((l, i) => (
                <div key={i} className="flex items-center justify-between border-b border-slate-100 last:border-0 py-2">
                  <div className="min-w-0">
                    <div className="text-slate-900 truncate text-[13px]">{l.first_name} {l.last_name?.[0]}. · <span className="text-slate-500">{l.sector?.replace(/-/g, " ")}</span></div>
                    <div className="text-[11px] text-slate-500">{l.city} · {new Date(l.created_at).toLocaleString("en-GB")}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[12px] font-mono tabular-nums text-slate-900">£{Number(l.publisher_earning_gbp || 0).toFixed(2)}</div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400">{l.status}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 text-right">
              <Link to="/publisher/leads" className="text-[12.5px] text-[#0055FF] font-medium hover:underline inline-flex items-center gap-1">
                View all leads <ArrowRight className="size-3" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function Kpi({ Icon, label, value, tint = "slate" }) {
  const tintCls = { slate: "text-slate-700", emerald: "text-emerald-700", brand: "text-[#0055FF]" }[tint];
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-soft">
      <Icon className={`size-4 ${tintCls}`} strokeWidth={1.7} />
      <div className="mt-3 text-2xl font-semibold tracking-tight text-slate-900 tabular-nums">{value}</div>
      <div className="text-[11px] uppercase tracking-widest text-slate-500 mt-1">{label}</div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-500 text-[12.5px]">{k}</span>
      <span className="text-slate-900 text-[13px] font-medium tabular-nums">{v}</span>
    </div>
  );
}
